import { useAuth } from "@/hooks/useAuth";

export type UserRole = "attendee" | "organizer" | "promoter" | null;

interface UserRoleData {
  role: UserRole;
  accountType: string | null;
  professionalType: string | null;
  approvalStatus: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  userId: string | null;
}

export const useUserRole = (): UserRoleData => {
  // Use the centralized auth hook - this prevents duplicate auth listeners and API calls
  const auth = useAuth();
  
  // Handle case where useAuth might not be ready
  const user = auth?.user ?? null;
  const profile = auth?.profile ?? null;
  const isLoading = auth?.isLoading ?? true;

  // Derive role from profile data
  const accountType = profile?.accountType ?? null;
  const professionalType = profile?.professionalType ?? null;
  const approvalStatus = profile?.approvalStatus ?? null;

  // Determine effective role
  let role: UserRole = null;
  if (profile) {
    if (accountType === "professional" && (approvalStatus === "approved" || approvalStatus === "pending")) {
      // Normalize legacy "event_organizer" to "organizer"
      const normalizedType = professionalType === "event_organizer" 
        ? "organizer" 
        : professionalType;
      role = normalizedType as UserRole;
    } else {
      // Attendees and rejected professionals
      role = "attendee";
    }
  }

  return { 
    role, 
    accountType, 
    professionalType, 
    approvalStatus, 
    isLoading, 
    isAuthenticated: !!user,
    userId: user?.id ?? null
  };
};
